import { Checkbox } from '@/components/ui/misc'

export interface WorkspaceFilterState {
  kind: 'experiment' | 'reference' | null
  fiber: string[]
  dyeSource: string[]
  mordant: string[]
}

export const EMPTY_WORKSPACE_FILTERS: WorkspaceFilterState = {
  kind: null,
  fiber: [],
  dyeSource: [],
  mordant: [],
}

interface Bucket {
  id: string
  name: string
  total: number
}

export interface WorkspaceFacets {
  dyeSources: Bucket[]
  fibers: Bucket[]
  mordants: Bucket[]
  kinds: { value: 'experiment' | 'reference'; total: number }[]
}

export function WorkspaceFilters({
  facets,
  filters,
  onChange,
}: {
  facets: WorkspaceFacets | undefined
  filters: WorkspaceFilterState
  onChange: (filters: WorkspaceFilterState) => void
}) {
  const toggle = (key: 'fiber' | 'dyeSource' | 'mordant', value: string) => {
    const current = filters[key]
    onChange({
      ...filters,
      [key]: current.includes(value)
        ? current.filter((entry) => entry !== value)
        : [...current, value],
    })
  }

  const active =
    filters.fiber.length +
    filters.dyeSource.length +
    filters.mordant.length +
    (filters.kind ? 1 : 0)

  return (
    <aside className="w-52 shrink-0">
      <div className="flex items-baseline justify-between pb-3">
        <h2 className="font-display text-xl font-semibold text-ink">Filters</h2>
        {active > 0 && (
          <button
            type="button"
            onClick={() => onChange(EMPTY_WORKSPACE_FILTERS)}
            className="text-[12px] font-semibold text-brand underline-offset-2 hover:underline"
          >
            Reset all
          </button>
        )}
      </div>

      <Group title="Record type">
        {(facets?.kinds ?? []).map((bucket) => (
          <Row
            key={bucket.value}
            label={bucket.value === 'reference' ? 'Reference' : 'Experiment'}
            total={bucket.total}
            checked={filters.kind === bucket.value}
            onToggle={() =>
              onChange({ ...filters, kind: filters.kind === bucket.value ? null : bucket.value })
            }
          />
        ))}
      </Group>

      <Group title="Fiber">
        {(facets?.fibers ?? []).map((bucket) => (
          <Row
            key={bucket.id}
            label={bucket.name}
            total={bucket.total}
            checked={filters.fiber.includes(bucket.id)}
            onToggle={() => toggle('fiber', bucket.id)}
          />
        ))}
      </Group>

      <Group title="Dye source">
        {(facets?.dyeSources ?? []).map((bucket) => (
          <Row
            key={bucket.id}
            label={bucket.name}
            total={bucket.total}
            checked={filters.dyeSource.includes(bucket.id)}
            onToggle={() => toggle('dyeSource', bucket.id)}
          />
        ))}
      </Group>

      <Group title="Mordant">
        {(facets?.mordants ?? []).map((bucket) => (
          <Row
            key={bucket.id}
            label={bucket.name}
            total={bucket.total}
            checked={filters.mordant.includes(bucket.id)}
            onToggle={() => toggle('mordant', bucket.id)}
          />
        ))}
      </Group>
    </aside>
  )
}

function Group({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="border-t border-line py-3">
      <p className="field-label pb-1.5">{title}</p>
      {children}
    </section>
  )
}

function Row({
  label,
  total,
  checked,
  onToggle,
}: {
  label: string
  total: number
  checked: boolean
  onToggle: () => void
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 py-[3px] text-[13px] text-ink-soft hover:text-ink">
      <Checkbox checked={checked} onCheckedChange={onToggle} />
      <span className="min-w-0 flex-1 truncate">{label}</span>
      <span className="text-[11px] text-ink-faint">{total}</span>
    </label>
  )
}
